(() => {
  const DRAWER = '#GalCartDrawer';
  const PAGE = '#GalCartPage';
  const WISHLIST_KEY = 'gal-wishlist';
  let noteTimer;
  let busy = false;

  const rootPath = () => window.Shopify?.routes?.root || '/';

  const readWishlist = () => {
    try {
      const raw = localStorage.getItem(WISHLIST_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (_err) {
      return [];
    }
  };

  const writeWishlist = (list) => {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    document.dispatchEvent(new CustomEvent('gal:wishlist:update', { detail: { list } }));
  };

  const setBusy = (on) => {
    busy = on;
    document.querySelectorAll('[data-gal-cart-line], [data-gal-cart-summary]').forEach((el) => {
      el.classList.toggle('is-busy', on);
    });
  };

  const syncCounts = (source) => {
    const countText =
      source?.querySelector('[data-gal-cart-count]')?.textContent ||
      source?.querySelector('[data-gal-cart-count-label]')?.textContent;
    if (!countText) return;
    document.querySelectorAll('[data-gal-cart-count]').forEach((el) => {
      const digits = String(countText).replace(/[^\d]/g, '');
      if (digits === '') return;
      const n = Number(digits);
      if (el.classList.contains('gal-header__cart-badge')) {
        el.textContent = String(n);
        el.hidden = n === 0;
      } else {
        el.textContent = `(${digits})`;
      }
    });
  };

  const afterMount = (root) => {
    if (!root) return;
    window.Galactica?.initVuMeters?.(root);
    window.Galactica?.observeVuStacks?.(root);
    syncCounts(root);
    bindStickyBar(root);
  };

  const replaceSection = (html, selector) => {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector(selector);
    const current = document.querySelector(selector);
    if (!next || !current) return current;
    const wasOpen = current.classList?.contains?.('is-open');
    current.replaceWith(next);
    const mounted = document.querySelector(selector);
    if (wasOpen && mounted) {
      mounted.classList.add('is-open');
      mounted.setAttribute('open', '');
      mounted.setAttribute('aria-hidden', 'false');
    }
    afterMount(mounted);
    return mounted;
  };

  const refreshCart = async ({ openDrawer = false } = {}) => {
    const sections = [];
    if (document.querySelector(DRAWER)) sections.push('cart-drawer');
    if (document.querySelector(PAGE)) sections.push('main-cart');
    if (!sections.length) return;

    const url = `${rootPath()}?sections=${sections.join(',')}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('cart refresh failed');
    const data = await res.json();

    if (data['cart-drawer']) replaceSection(data['cart-drawer'], DRAWER);
    if (data['main-cart']) replaceSection(data['main-cart'], PAGE);

    if (openDrawer && window.Galactica?.settings?.cartType === 'drawer') {
      document.dispatchEvent(new CustomEvent('gal:cart:open'));
    }
  };

  const changeLine = async (key, quantity) => {
    const res = await fetch(window.Galactica.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.description || err?.message || 'update failed');
    }
    return res.json();
  };

  const addVariant = async (variantId, quantity = 1) => {
    const res = await fetch(window.Galactica.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: Number(quantity) || 1 }] }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.description || err?.message || 'add failed');
    }
    return res.json();
  };

  const showLineError = (line, message) => {
    if (!line) return;
    let el = line.querySelector('[data-gal-cart-error]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'gal-cart-line__error';
      el.dataset.galCartError = '';
      line.querySelector('.gal-cart-line__body')?.appendChild(el);
    }
    el.textContent = message || 'Mise à jour impossible. Réessaie.';
  };

  const onCartPage = () => Boolean(document.querySelector(PAGE));

  const runUpdate = async (fn, { openDrawer = !onCartPage() } = {}) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      await refreshCart({ openDrawer });
    } catch (err) {
      console.warn('[gal-cart]', err);
      const msg = err?.message || '';
      const drawerOpen = Boolean(document.querySelector(`${DRAWER}.is-open, ${DRAWER}[open]`));
      if (/sold|épuis|unavailable|inventaire|stock/i.test(msg)) {
        document
          .querySelectorAll('[data-gal-cart-line].is-busy')
          .forEach((line) => showLineError(line, 'Format indisponible pour le moment.'));
      } else if (drawerOpen) {
        document
          .querySelectorAll('[data-gal-cart-line].is-busy')
          .forEach((line) => showLineError(line, msg || 'Mise à jour impossible. Réessaie.'));
      } else if (!onCartPage()) {
        window.location.href = window.Galactica.routes.cart_url;
      }
    } finally {
      setBusy(false);
    }
  };

  const bindStickyBar = (root) => {
    const page = root?.matches?.(PAGE) ? root : document.querySelector(PAGE);
    if (!page) return;
    const sticky = page.querySelector('[data-gal-cart-sticky]');
    const summary = page.querySelector('[data-gal-cart-summary]');
    if (!sticky || !summary || sticky.dataset.bound === '1') return;
    sticky.dataset.bound = '1';
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        const show = !entry.isIntersecting && window.matchMedia('(max-width: 899px)').matches;
        sticky.hidden = !show;
      },
      { rootMargin: '0px 0px -20% 0px', threshold: 0.05 }
    );
    io.observe(summary);
  };

  document.addEventListener('gal:cart:refresh', () => {
    refreshCart({ openDrawer: false }).catch(() => {});
  });

  document.addEventListener('input', (e) => {
    const noteEl = e.target.closest('[data-gal-cart-note]');
    if (!noteEl || !window.Galactica?.routes?.cart_update_url) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(async () => {
      try {
        await fetch(window.Galactica.routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: noteEl.value }),
        });
      } catch (_err) {
        /* ignore */
      }
    }, 450);
  });

  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-gal-cart-variant]');
    if (!select) return;
    const lineKey = select.dataset.lineKey;
    const quantity = Number(select.dataset.quantity || 1);
    const nextId = select.value;
    const currentId = select.dataset.currentVariant;
    if (!lineKey || !nextId || nextId === currentId) return;
    runUpdate(async () => {
      await changeLine(lineKey, 0);
      await addVariant(nextId, quantity);
    });
  });

  document.addEventListener('click', async (e) => {
    const addRec = e.target.closest('[data-gal-cart-add]');
    if (addRec) {
      e.preventDefault();
      const variantId = addRec.dataset.variantId;
      if (!variantId) return;
      runUpdate(async () => {
        await addVariant(variantId, 1);
      }, { openDrawer: window.Galactica?.settings?.cartType === 'drawer' && !onCartPage() });
      return;
    }

    const save = e.target.closest('[data-gal-cart-save]');
    if (save) {
      e.preventDefault();
      const handle = save.dataset.productHandle;
      const key = save.dataset.lineKey;
      if (!handle || !key) return;
      const list = readWishlist();
      if (!list.includes(handle)) list.push(handle);
      writeWishlist(list);
      runUpdate(async () => {
        await changeLine(key, 0);
      });
      return;
    }

    const remove = e.target.closest('[data-gal-cart-remove]');
    const minus = e.target.closest('[data-gal-cart-minus]');
    const plus = e.target.closest('[data-gal-cart-plus]');
    if (!remove && !minus && !plus) return;
    e.preventDefault();
    const btn = remove || minus || plus;
    const key = btn.dataset.lineKey;
    const qty = Number(btn.dataset.quantity || 0);
    let nextQty = qty;
    if (remove) nextQty = 0;
    if (minus) nextQty = Math.max(0, qty - 1);
    if (plus) nextQty = qty + 1;
    if (!key) return;
    runUpdate(async () => {
      await changeLine(key, nextQty);
    });
  });

  // Initial sticky bind on cart page
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      afterMount(document.querySelector(PAGE));
      afterMount(document.querySelector(DRAWER));
    });
  } else {
    afterMount(document.querySelector(PAGE));
    afterMount(document.querySelector(DRAWER));
  }
})();
